import { TIER_NAMES } from "MVVM/Utilities/SubscriptionTiers";
import placeholderBanner from "MVVM/Assets/Homepage/cs-carousel.webp";
import {
  ICompetition,
  ICompetitionPrize,
  IClipRequirement,
  IEligibilityError,
  IRankingConfig,
  TSide,
} from "./@types";

// The backend only sends a raw check name in `field` (e.g.
// "prior_winner_cooldown"), not display copy — reaching this state is meant
// to be rare since the frontend should already be preventing the attempt.
const ELIGIBILITY_ERROR_MESSAGES: Record<string, string> = {
  prior_winner_cooldown:
    "You've recently won a competition and are in a cooldown period before you can enter another.",
};

export const GENERIC_INELIGIBLE_MESSAGE =
  "You're not eligible to enter this competition.";

// We know the cooldown window's length (entryRequirement.priorWinnerCooldownDays,
// already loaded with the competition) but not when the user's own win landed,
// so we can only say how long the window is — not how many days they have left.
export const formatEligibilityError = (
  field: string,
  priorWinnerCooldownDays?: number | null,
): string => {
  if (field === "prior_winner_cooldown" && priorWinnerCooldownDays) {
    const n = priorWinnerCooldownDays;
    return `You've won a competition within the last ${n} day${
      n === 1 ? "" : "s"
    }, which puts you in this competition's cooldown period.`;
  }
  return ELIGIBILITY_ERROR_MESSAGES[field] ?? GENERIC_INELIGIBLE_MESSAGE;
};

export const getIneligibilityMessages = (
  errors: IEligibilityError[] | null | undefined,
  priorWinnerCooldownDays?: number | null,
): string[] => {
  if (!errors?.length) return [GENERIC_INELIGIBLE_MESSAGE];
  return Array.from(
    new Set(
      errors.map((e) =>
        formatEligibilityError(e.field, priorWinnerCooldownDays),
      ),
    ),
  );
};

const SIDE_LABELS: Record<TSide, string> = {
  T: "Terrorist",
  CT: "Counter-Terrorist",
};

const SLUG_LABEL_OVERRIDES: Record<string, string> = {
  ars: "ARs",
  smgs: "SMGs",
  lmgs: "LMGs",
  awp: "AWP",
  "ak-47": "AK-47",
  m4a4: "M4A4",
  "m4a1-s": "M4A1-S",
  "ssg-08": "SSG 08",
  "r8-revolver": "R8 Revolver",
};

const prettifySlug = (slug: string): string => {
  const override = SLUG_LABEL_OVERRIDES[slug.toLowerCase()];
  if (override) return override;
  return slug
    .split(/[-_\s]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const getClipRestrictionLabels = (
  clipReq: IClipRequirement | null | undefined,
): string[] => {
  if (!clipReq) return [];
  const labels: string[] = [];

  if (clipReq.map) labels.push(`${prettifySlug(clipReq.map)} only`);

  if (clipReq.weaponType) {
    labels.push(`${prettifySlug(clipReq.weaponType)} only`);
  } else if (clipReq.weapon) {
    labels.push(`${prettifySlug(clipReq.weapon)} only`);
  }

  if (clipReq.clutch) {
    labels.push("Clutch rounds only");
  } else if (clipReq.situation) {
    labels.push(`${prettifySlug(clipReq.situation)} only`);
  }

  if (clipReq.economy)
    labels.push(`${prettifySlug(clipReq.economy)} rounds only`);

  if (clipReq.side) labels.push(`${SIDE_LABELS[clipReq.side]} side only`);

  if (clipReq.command) labels.push(`Created with ${clipReq.command}`);

  return labels;
};

export interface IScoringSummary {
  headline: string;
  detail: string;
  sentence: string;
}

export const getScoringSummary = (
  rankingConfig: IRankingConfig | null | undefined,
  clipCap: number | null | undefined,
): IScoringSummary | null => {
  const bestOf = rankingConfig?.topNScoring && clipCap ? clipCap : null;
  const capped = `You can submit more, but only your ${bestOf} highest-scoring clips count.`;

  let headline: string;
  let detail: string;

  switch (rankingConfig?.allstarScoreType) {
    case "total":
      headline = bestOf
        ? `Sum of your best ${bestOf} clips`
        : "Sum of all your clips";
      detail = bestOf
        ? capped
        : "Every clip you submit adds to your score, so more qualifying clips can only help.";
      break;
    case "average":
      headline = bestOf
        ? `Average of your best ${bestOf} clips`
        : "Average of all your clips";
      detail = bestOf
        ? capped
        : "A low-scoring clip pulls your average down, so submit selectively.";
      break;
    default:
      return null;
  }

  return {
    headline,
    detail,
    sentence: `Your score is the ${headline.toLowerCase()}. ${detail}`,
  };
};

export interface ISignatureLoadoutMeta {
  metadataDisplayName?: string | null;
  metadataThemeName?: string | null;
  metadataStyleName?: string | null;
}

/* clean up loadout display names for competition signatures */
export const formatSignatureName = (
  loadout: ISignatureLoadoutMeta,
  fallback = "signature",
): string => {
  const rawBase =
    loadout.metadataDisplayName || loadout.metadataThemeName || fallback;
  const base = rawBase.startsWith("Competitions")
    ? rawBase.split(/[/.·]/).pop()?.trim() || rawBase
    : rawBase;
  const style = loadout.metadataStyleName;
  return style && style.toLowerCase() !== base.toLowerCase()
    ? `${base} · ${style}`
    : base;
};

export const COMPETITION_PLACEHOLDER_BANNER = placeholderBanner;

export type TCompetitionPhase = "upcoming" | "active" | "ended";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const isEndingWithinDays = (
  competition: ICompetition,
  days: number,
): boolean => {
  const end = new Date(competition.endsAt).getTime();
  const diff = end - Date.now();
  return diff > 0 && diff <= days * DAY;
};

export const getCompetitionPhase = (
  competition: Pick<ICompetition, "startsAt" | "endsAt">,
): TCompetitionPhase => {
  const now = Date.now();
  const start = new Date(competition.startsAt).getTime();
  const end = new Date(competition.endsAt).getTime();
  if (end <= now) return "ended";
  if (start > now) return "upcoming";
  return "active";
};

const plural = (n: number, unit: string) => `${n} ${unit}${n !== 1 ? "s" : ""}`;

export const getCountdownLabel = (
  competition: ICompetition,
  phase: TCompetitionPhase = getCompetitionPhase(competition),
): string => {
  const now = Date.now();
  const start = new Date(competition.startsAt).getTime();
  const end = new Date(competition.endsAt).getTime();

  if (phase === "ended") {
    const date = new Date(end).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `Ended ${date}`;
  }

  if (phase === "upcoming") {
    const diff = start - now;
    const days = Math.ceil(diff / DAY);
    if (days > 1) return `Starts in ${plural(days, "day")}`;
    const hours = Math.ceil(diff / HOUR);
    return `Starts in ${plural(hours, "hour")}`;
  }

  const diff = end - now;
  const days = Math.floor(diff / DAY);
  const hours = Math.floor((diff % DAY) / HOUR);
  if (days > 0) return `${days}d ${hours}h left`;
  const minutes = Math.floor((diff % HOUR) / MINUTE);
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

export const getSubscriptionLabel = (prize: ICompetitionPrize): string => {
  if (!prize.tier || !prize.duration) return "Allstar Subscription";
  /* TIER_NAMES is the canonical user-facing tier map (ESSENTIALS → Basic,
   * PRO → Standard, CREATOR_PLAT → Unlimited). The previous local
   * TIER_DISPLAY here used the old internal codenames. */
  const tier = TIER_NAMES[prize.tier] ?? prize.tier;
  const duration = prize.duration === "year" ? "1 year" : "1 month";
  return `${duration} ${tier}`;
};

export type RankAccent = { color: string; bg: string; bgHover: string };

export const rankAccent = (
  rank: number | null,
  theme: { colors: Record<string, string> },
): RankAccent | null => {
  if (rank === 1)
    return {
      color: theme.colors.rare,
      bg: "rgba(208, 106, 174, 0.14)",
      bgHover: "rgba(208, 106, 174, 0.22)",
    };
  if (rank === 2)
    return {
      color: theme.colors.turbo,
      bg: "rgba(113, 170, 255, 0.12)",
      bgHover: "rgba(113, 170, 255, 0.2)",
    };
  if (rank === 3)
    return {
      color: theme.colors.envy,
      bg: "rgba(77, 189, 148, 0.12)",
      bgHover: "rgba(77, 189, 148, 0.2)",
    };
  return null;
};

export const getPrizeLabel = (
  prizes: ICompetitionPrize[] | null | undefined,
): string => {
  if (!prizes || prizes.length === 0) return "Just for fun";
  const first = prizes[0];
  if (first.description) return first.description;
  if (first.prizeType === "subscription") return getSubscriptionLabel(first);
  if (first.prizeType === "custom") return "Custom Prize";
  return "Just for fun";
};

export const formatPrizeLabel = (prize: ICompetitionPrize): string => {
  if (prize.description) return prize.description;
  if (prize.prizeType === "subscription") return getSubscriptionLabel(prize);
  if (prize.prizeType === "custom") return "Custom Prize";
  return "Just for fun";
};
